import { BookingTransactionType, DateFormats } from '../constants/securspace-constants';
import { formatMoneyAddDecimalPointAndThousands } from '../../util/PaymentUtils';
import type { BuyerInvoicesSummary } from '../../types/model/valueObject/invoice/BuyerInvoicesSummary';
import { getInvoicesSummary } from './requests/invoice-lookup-requests';
import { isoDateRange } from './requests/apiDates';
import { hasPeriodEnded } from './periodParser';
import type { KnowledgeAnswer, KnowledgeContext, KnowledgeQuery } from './knowledgeRegistry';
import moment from 'moment';

interface InvoiceTotals {
  baseCharges: number;
  processorFees: number;
  taxes: number;
  totalPaid: number;
}

const OVERAGE_TRANSACTION_TYPES = [
  BookingTransactionType.OVERAGE_CHARGE,
  BookingTransactionType.DAILY_OVERAGE_CHARGE,
  BookingTransactionType.MANUAL_OVERAGE_CHARGE,
] as const;

const money = (amount: number): string => formatMoneyAddDecimalPointAndThousands(amount);

const numeric = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const fromSummary = (body: BuyerInvoicesSummary | undefined): InvoiceTotals => ({
  baseCharges: numeric(body?.grossAmount),
  processorFees: numeric(body?.paymentProcessorFees),
  taxes: numeric(body?.transactionTaxAmount),
  // buyerAmount is the full amount charged (grossAmount + tax + processor fee), so it is the
  // total as-is; adding the fees and tax to it again would count them twice.
  totalPaid: numeric(body?.buyerAmount),
});

const addTotals = (left: InvoiceTotals, right: InvoiceTotals): InvoiceTotals => ({
  baseCharges: left.baseCharges + right.baseCharges,
  processorFees: left.processorFees + right.processorFees,
  taxes: left.taxes + right.taxes,
  totalPaid: left.totalPaid + right.totalPaid,
});

const EMPTY_TOTALS: InvoiceTotals = {
  baseCharges: 0,
  processorFees: 0,
  taxes: 0,
  totalPaid: 0,
};

const fetchTotals = async (params: {
  startDate: string;
  endDate: string;
  locationId?: string;
}): Promise<InvoiceTotals> => {
  const summaries = await Promise.all(
    OVERAGE_TRANSACTION_TYPES.map(async (transactionType) =>
      fromSummary(await getInvoicesSummary('buyer', { 
        startDate: moment(params.startDate).format(DateFormats.DAY),
        endDate: moment(params.endDate).format(DateFormats.DAY),
        locationId: params.locationId ?? '',
        transactionType 
      })),
    ),
  );

  return summaries.reduce(addTotals, EMPTY_TOTALS);
};

const answer = async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const { period, targets, wholeAccount } = context;
  const single = !wholeAccount && targets.length === 1 ? targets[0] : null;
  const scopePhrase = single ? `at **${single.name}** ` : '';
  const totals = await fetchTotals({
    ...isoDateRange(period),
    ...(single ? { locationId: single.id } : {}),
  });

  if (totals.totalPaid === 0) {
    const text = single
      ? `No overage invoices ${scopePhrase}were paid ${period.label}.`
      : `You did not pay any overage invoices ${period.label}.`;

    if (!single && !wholeAccount && targets.length > 1) {
      return {
        text:
          `${text} That covers every location — the invoice summary can only filter one ` +
          'location at a time, so ask about a single one for its own figure.',
      };
    }

    return { text };
  }

  const verb = hasPeriodEnded(period) ? 'totaled' : 'have totaled';
  const text = single
    ? `Overage invoices ${scopePhrase}${verb} **${money(totals.totalPaid)}** ${period.label}.`
    : `Your overage invoices ${verb} **${money(totals.totalPaid)}** ${period.label}.`;
  const answerWithBreakdown: KnowledgeAnswer = {
    text,
    breakdown: [
      { label: 'Overage charges', value: money(totals.baseCharges) },
      { label: 'Processor fees', value: money(totals.processorFees) },
      { label: 'Taxes', value: money(totals.taxes) },
      { label: 'Total paid', value: money(totals.totalPaid) },
    ],
  };

  if (!single && !wholeAccount && targets.length > 1) {
    return {
      ...answerWithBreakdown,
      text:
        `${text} That covers every location — the invoice summary can only filter one ` +
        'location at a time, so ask about a single one for its own figure.',
    };
  }

  return answerWithBreakdown;
};

export const buyerOverageInvoiceAmountQuery: KnowledgeQuery = {
  id: 'buyerOverageInvoiceAmount',
  audiences: ['buyer'],
  synonyms: [
    'how much did i pay for overages',
    'how much did i pay in overages',
    'how much was i charged for overages',
    'what did i pay for overages',
    'what was my overage bill',
    'what were my overage invoices',
    'how much are my overage invoices',
    'total overage invoice amount',
    'total amount paid for overages',
    'overage invoice total',
  ],
  requires: [
    ['overage', 'overages'],
    ['pay', 'paid', 'charge', 'charged', 'cost', 'billed', 'bill', 'invoice', 'invoices'],
    ['much', 'amount', 'total', 'money', 'dollars'],
  ],
  example: 'how much did I pay for overages this week',
  description:
    'How much a buyer paid in overage invoices over a time period. Totals the money on ' +
    'overage-payment invoice types (overage and daily overage), not the count of overage ' +
    'space-days. Use for "how much did I pay for overages" style questions.',
  answer,
};

export const __testing = {
  OVERAGE_TRANSACTION_TYPES,
  fromSummary,
  fetchTotals,
};
